import { useEffect, useState } from "react";
import { ArrowLeft, Headset } from "lucide-react";
import { channelApi, errText } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";

const REGION_NAMES = {
  AF: "Afghanistan",
  BY: "Belarus",
  CF: "Central African Republic",
  CD: "Democratic Republic of the Congo",
  CU: "Cuba",
  IR: "Iran",
  LB: "Lebanon",
  CN: "Mainland China",
  ML: "Mali",
  MM: "Myanmar (Burma)",
  NI: "Nicaragua",
  KP: "North Korea",
  RU: "Russian Federation",
  SO: "Somalia",
  SS: "South Sudan",
  SD: "Sudan",
  SY: "Syrian Arab Republic",
  VE: "Venezuela",
};

const COUNTRY_NAMES = new Map(COUNTRIES.map((country) => [country.code, country.name]));

export default function ChannelRestrictedRegions() {
  const [blockedCountries, setBlockedCountries] = useState(null);
  const [effectiveAt, setEffectiveAt] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    channelApi.get("/availability")
      .then(({ data }) => {
        setBlockedCountries(Array.isArray(data.blocked_countries) ? data.blocked_countries : []);
        setEffectiveAt(data.effective_at || "");
      })
      .catch((cause) => setError(errText(cause)));
  }, []);

  const effectiveDate = effectiveAt && !Number.isNaN(new Date(effectiveAt).getTime())
    ? new Intl.DateTimeFormat(undefined, { timeZone: "UTC", day: "numeric", month: "long", year: "numeric" }).format(new Date(effectiveAt))
    : null;

  return (
    <main className="min-h-screen bg-[#0A0A0A] px-5 py-8 text-white sm:px-8" data-testid="channel-restricted-regions-page">
      <div className="mx-auto max-w-4xl">
        <header className="flex items-center justify-between border-b border-white/15 pb-5">
          <a href="/channel" className="flex items-center gap-3 font-display text-lg font-extrabold"><img src="/assets/logo.png" alt="Cresway Media" className="h-10 w-10 object-contain" />Cresway Channel</a>
          <a href="/channel/support" className="inline-flex items-center gap-2 text-sm font-semibold text-white/65 underline underline-offset-4 hover:text-white"><Headset className="h-4 w-4" />Support</a>
        </header>
        <section className="py-12 sm:py-16">
          <a href="/channel" className="inline-flex items-center gap-2 text-sm font-semibold text-white/60 hover:text-white"><ArrowLeft className="h-4 w-4" />Back to Channel</a>
          <p className="mt-10 font-mono-label text-xs uppercase tracking-[0.2em] text-[#E62314]">Cresway Channel</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold sm:text-5xl">Restricted Regions</h1>
          <p className="mt-4 max-w-2xl text-sm leading-6 text-white/65">Cresway Channel is not available in the following countries and regions. This list reflects the restrictions currently in effect.</p>
          {effectiveDate && <p className="mt-3 text-sm text-white/55">Restrictions effective from {effectiveDate}</p>}
          {blockedCountries === null && !error && <p className="mt-8 text-sm text-white/60" role="status">Loading restricted regions…</p>}
          {error && <p className="mt-8 border border-[#E62314]/50 bg-[#E62314]/10 p-4 text-sm" role="alert">We couldn’t load the restricted regions list right now. Please try again later.</p>}
          {blockedCountries?.length > 0 && <ul className="mt-8 grid gap-x-12 border-y border-white/15 py-5 sm:grid-cols-2" data-testid="channel-restricted-region-list">
            {blockedCountries.map((code) => <li key={code} className="border-b border-white/10 py-3 text-sm text-white/80">{REGION_NAMES[code] || COUNTRY_NAMES.get(code) || code}</li>)}
          </ul>}
          {blockedCountries?.length === 0 && <p className="mt-8 border-y border-white/15 py-5 text-sm text-white/75">No countries or regions are currently restricted.</p>}
        </section>
      </div>
    </main>
  );
}